import { forwardRef, SyntheticEvent } from "react";
import {
  Pressable,
  StyleProp,
  StyleSheet,
  TextInput,
  TextInputProps,
  TextStyle,
  TouchableOpacity,
  View,
  ViewProps,
  ViewStyle,
} from "react-native";
import ReactDatePicker, { ReactDatePickerProps } from "react-datepicker";
import * as DocumentPicker from "expo-document-picker";

import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";

import "src/util/datePicker.css";
import { PdDivider } from "../layout/table";
import { PdStack, PdText } from "../layout";
import { PdButton, PdButtonLabel, tPdButtonProps } from "./button";
import { PdAttachment, PdCheck, PdClose } from "../Icons";
import { getSafeUrl } from "src/util/functions";
import { openUrlInNewTab } from "src/util/linking";

export type tPdInputProps = TextInputProps & { isDisabled?: boolean };

export type tPdInputNumberProps = Omit<TextInputProps, "value" | "onChangeText"> & {
  value?: number | null;
  onChangeNumber?: (value: number | null) => void;
};

export type tPdInputFloatProps = TextInputProps;

export type tPdInputPercentageProps = Omit<TextInputProps, "value" | "onChangeText"> & {
  value?: number | null;
  onChangeNumber?: (value: number | null) => void;
  isDisabled?: boolean;
};

type tPdDateInputProps = ReactDatePickerProps & { style?: StyleProp<TextStyle> };

export type tPdRangeDateInputProps = Omit<ReactDatePickerProps, "disabled"> & {
  isDisabled?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  style?: StyleProp<TextStyle>;
  period: [Date, Date];
};

const PdInput = forwardRef<TextInput, tPdInputProps>((props, ref) => {
  const { style, isDisabled, onChangeText, ...rest } = props;

  const handleOnChangeText = (text: string) => {
    if (!isDisabled) {
      onChangeText?.(text);
    }
  };

  return (
    <TextInput
      ref={ref}
      style={[styles.pdInput, isDisabled ? styles.opacityMd : {}, style]}
      onChangeText={handleOnChangeText}
      editable={!isDisabled}
      {...rest}
    />
  );
});
PdInput.displayName = "PdInput";
export { PdInput };

const PdInputNumber = forwardRef<TextInput, tPdInputNumberProps>((props, ref) => {
  const { value, onChangeNumber, style, maxLength, ...rest } = props;

  const handleOnChangeText = (text: string) => {
    if (/^[0-9]*$/gm.test(text)) {
      if (text.charAt(0) === "0" && text.length > 1) text = text.substring(1);
      if (!maxLength || text.length < maxLength) {
        onChangeNumber?.(text.length === 0 ? null : Number.parseFloat(text));
      }
    }
  };

  return (
    <TextInput
      ref={ref}
      style={[styles.pdInput, style]}
      value={value === null || value === undefined ? "" : `${value}`}
      onChangeText={handleOnChangeText}
      keyboardType="numeric"
      maxLength={maxLength}
      {...rest}
    />
  );
});
PdInputNumber.displayName = "PdInputNumber";
export { PdInputNumber };

const PdInputFloat = forwardRef<TextInput, tPdInputFloatProps>((props, ref) => {
  const { value, onChangeText, style, ...rest } = props;

  const handleOnChangeText = (text: string) => {
    if (/^[0-9]*\.?[0-9]*$/gm.test(text)) {
      if (text.charAt(0) === "0" && text.length > 1) text = text.substring(1);
      else if (!text.length) text = "0";
      onChangeText?.(text);
    }
  };

  return (
    <TextInput
      ref={ref}
      value={value}
      style={[styles.pdInput, style]}
      onChangeText={handleOnChangeText}
      keyboardType="numeric"
      {...rest}
    />
  );
});
PdInputFloat.displayName = "PdInputFloat";
export { PdInputFloat };

const PdInputPercentage = forwardRef<TextInput, tPdInputPercentageProps>((props, ref) => {
  const { value, onChangeNumber, style, isDisabled, ...rest } = props;

  const handleOnChangeNumber = (newValue: number | null) => {
    if (newValue === null) onChangeNumber?.(newValue);
    if (newValue !== null && newValue >= 0 && newValue <= 100 && !isDisabled) {
      onChangeNumber?.(newValue);
    }
  };

  return (
    <PdInputNumber
      ref={ref}
      style={[styles.pdInput, isDisabled ? styles.opacityMd : {}, style]}
      value={value}
      onChangeNumber={handleOnChangeNumber}
      keyboardType="numeric"
      editable={!isDisabled}
      maxLength={5}
      {...rest}
    />
  );
});
PdInputPercentage.displayName = "PdInputPercentage";
export { PdInputPercentage };

export const PdDateInput = forwardRef<TextInput, tPdDateInputProps>((props, ref) => {
  const { style, ...rest } = props;
  return <ReactDatePicker dateFormat="MM/dd/yyyy" customInput={<PdInput style={style} ref={ref} />} {...rest} />;
});

export const PdDateRangeInput = forwardRef<TextInput, tPdRangeDateInputProps>((props, ref) => {
  const { containerStyle, style, period, onChange, isDisabled, ...rest } = props;

  const handleOnChangeStartPeriod = (date: Date, event: SyntheticEvent) => {
    onChange([date, period[1]], event);
  };

  const handleOnChangeEndPeriod = (date: Date, event: SyntheticEvent) => {
    onChange([period[0], date], event);
  };

  return (
    <View style={[styles.pdDateRangeInputContainer, containerStyle]}>
      <ReactDatePicker
        selectsStart
        selected={period[0]}
        startDate={period[0]}
        endDate={period[1]}
        onChange={handleOnChangeStartPeriod}
        dateFormat="MM/dd/yyyy"
        customInput={
          <TextInput style={[styles.pdDateRangeInput, isDisabled ? styles.opacityMd : {}, style]} ref={ref} />
        }
        disabled={isDisabled}
        {...rest}
      />
      <PdDivider style={styles.pdDateRangeInputDivider} />
      <ReactDatePicker
        selectsEnd
        selected={period[1]}
        startDate={period[0]}
        endDate={period[1]}
        minDate={period[0]}
        onChange={handleOnChangeEndPeriod}
        dateFormat="MM/dd/yyyy"
        customInput={<TextInput style={[styles.pdDateRangeInput, isDisabled ? styles.opacityMd : {}, style]} />}
        disabled={isDisabled}
        {...rest}
      />
    </View>
  );
});

export type tPdInputPdfValue = {
  uri?: string;
  file?: File;
  name: string;
} | null;

export type tPdInputPdf = Omit<tPdInputProps, "value" | "onChangeText" | "onChange" | "style"> &
  Pick<ViewProps, "style"> & {
    value?: tPdInputPdfValue;
    onChange?: (value: tPdInputPdfValue) => void;
    placeholder?: string;
    isClearable?: boolean;
    isMobile?: boolean;
  };

const PdInputPdf = forwardRef<View, tPdInputPdf>((props, ref) => {
  const {
    value,
    onChange,
    style,
    placeholder = "Select the document",
    isDisabled,
    isClearable = true,
    isMobile,
  } = props;

  const handleOnPress: tPdButtonProps["onPress"] = async () => {
    const documentResult = await DocumentPicker.getDocumentAsync({ type: "application/pdf" });
    if (documentResult.type === "success") {
      onChange?.({ uri: documentResult.uri, file: documentResult.file, name: documentResult.name });
    }
  };

  const handleOnPressFile = () => {
    openUrlInNewTab(getSafeUrl(value?.uri));
  };

  const handleOnClear = () => {
    onChange?.(null);
  };

  return (
    <PdStack
      direction={isMobile ? "vertical" : "horizontal"}
      style={[isMobile ? {} : styles.alignCenter, style]}
      ref={ref}
    >
      <PdButton
        size="sm"
        colorScheme="pink"
        isDisabled={isDisabled}
        onPress={handleOnPress}
        rightIcon={<PdAttachment style={styles.sizeMd} />}
      >
        <PdButtonLabel>Upload</PdButtonLabel>
      </PdButton>
      <View style={[styles.flex1, styles.flexRow, styles.alignCenter]}>
        {value ? (
          <TouchableOpacity style={styles.pdInputPdfFileUploaded} onPress={handleOnPressFile}>
            <PdText>{value.name}</PdText>
            <View style={styles.pdInputPdfIconContainer}>
              <PdCheck color={kColors.white} style={styles.pdInputPdfIcon} />
            </View>
          </TouchableOpacity>
        ) : (
          <PdText style={styles.placeholder}>{placeholder ?? ""}</PdText>
        )}
        {isClearable && value && (
          <Pressable onPress={handleOnClear} focusable={false} style={styles.pdCloseButton}>
            <PdClose focusable={false} />
          </Pressable>
        )}
      </View>
    </PdStack>
  );
});
PdInputPdf.displayName = "PdInput";
export { PdInputPdf };

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  opacityMd: {
    opacity: 0.4,
  },
  pdCloseButton: {
    height: "100%",
    justifyContent: "center",
    marginHorizontal: kStyleConst.kSpacing,
    width: 12,
  },
  pdDateRangeInput: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontWeight: "300",
    letterSpacing: 1.1,
    lineHeight: 25,
    paddingHorizontal: 2 * kStyleConst.kSpacing,
    paddingVertical: 1 * kStyleConst.kSpacing,
    width: "100%",
  },
  pdDateRangeInputContainer: {
    alignItems: "center",
    borderColor: kColors.gray300,
    borderWidth: 1,
    flexDirection: "row",
  },
  pdDateRangeInputDivider: {
    height: "60%",
    marginHorizontal: 2 * kStyleConst.kSpacing,
    width: StyleSheet.hairlineWidth,
  },
  pdInput: {
    borderColor: kColors.gray300,
    borderWidth: 1,
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontWeight: "300",
    letterSpacing: 1.1,
    lineHeight: 25,
    paddingHorizontal: 2 * kStyleConst.kSpacing,
    paddingVertical: 1 * kStyleConst.kSpacing,
  },
  pdInputPdfFileUploaded: {
    alignItems: "center",
    flex: 1,
    flexDirection: "row",
  },
  pdInputPdfIcon: {
    height: 10,
    width: 10,
  },
  pdInputPdfIconContainer: {
    alignItems: "center",
    backgroundColor: kColors.green,
    borderRadius: 999,
    height: 20,
    justifyContent: "center",
    marginLeft: 2 * kStyleConst.kSpacing,
    width: 20,
  },
  placeholder: {
    color: kColors.gray,
  },
  sizeMd: {
    height: 15,
    width: 15,
  },
  widthFull: {
    width: "100%",
  },
});
